import React, { useState, useEffect, useMemo, type Key } from 'react';
import { Table, Radio, Tag, Button, Space, Pagination, message, Typography, Card, Tabs, Row, Col, Statistic } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EditOutlined, PrinterOutlined, DollarOutlined, FileTextOutlined, CalendarOutlined, BarChartOutlined } from '@ant-design/icons';
import { theme } from '../../../theme';

interface ComprobanteEgreso {
  id: number;
  tipo: string;
  nro_comprobante: string;
  fecha_emision: string;
  sector_nombre: string;
  equipo_nombre: string;
  motor_nombre: string;
  subtotal: number;
  alta: number;
  autorizada: boolean;
}

const ListarEgresos: React.FC = () => {
  const [ordenes, setOrdenes] = useState<ComprobanteEgreso[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, isLoading ,getAccessTokenSilently } = useAuth0();
  //const { getAccessTokenSilently } = useAuth0();
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Asegúrate de que user esté definido antes de acceder a sus propiedades
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;

  useEffect(() => {
    if (isLoading || !user) return; // Espera a que el usuario esté cargado
    console.log('empresa', empresa);
    console.log('user', user);
    fetchOrdenes();
  }, [isLoading, user, empresa, getAccessTokenSilently]);

  const fetchOrdenes = async () => {
    if (!empresa) {
      console.error('Empresa no definida');
      return;
    }

    try {
      setLoading(true);
      setSelectedRowId(null);
      const token = await getAccessTokenSilently();
      const ruta = `${process.env.REACT_APP_INSUMOS_URL}/listar_egresos/${empresa}/${sucursal}`;
      console.log('ruta', ruta);
      const response = await fetch(ruta, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Error al cargar los egresos');

      const data = await response.json();
      console.log('egresos cargados:', data);
      setOrdenes(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los egresos');
    } finally {
      setLoading(false);
    }
  };

  const handleRadioChange = (id: number) => {
    setSelectedRowId(id);
    console.log('Seleccionado:', id);
  };

  const handleEditar = () => {
    if (selectedRowId !== null) {
      console.log('Editar:', selectedRowId);
      navigate(`/editar_egresos/${selectedRowId}`);
    }
  };

  const handleImprimir = () => {
    if (selectedRowId !== null) {
      navigate(`/imprimir_egresos/${selectedRowId}`);
    }
  };

  
  const handleOrdenCompraNueva = () => {
    navigate('/crear_egresos');
  };

  const columns: ColumnsType<ComprobanteEgreso> = [
    {
      title: '',
      key: 'radio',
      fixed: 'left',
      width: 10,
      render: (_, record) => (
        <Radio 
          value={record.id}
          checked={selectedRowId === record.id}
          onChange={() => handleRadioChange(record.id)}
          className="table-radio"
        />
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 20,
    },
    {
      title: 'NºComprobante',
      dataIndex: 'nro_comprobantes',
      key: 'nro_comprobantes',
      width: 35,
    },
    {
      title: 'Emisión',
      dataIndex: 'fecha_emision',
      key: 'fecha_emision',
      width: 35,
      render: (fecha: string) => new Date(fecha).toLocaleDateString(),
      sorter: (a, b) => 
        new Date(a.fecha_emision).getTime() - new Date(b.fecha_emision).getTime(),
    },
    {
      title: 'Sector',
      dataIndex: 'sector_nombre',
      key: 'sector_nombre',
      width: 35,
      filters: Array.from(new Set(ordenes.map(orden => orden.sector_nombre)))
        .map(sector => ({ text: sector, value: sector })),
      onFilter: (value: boolean | Key | string, record: ComprobanteEgreso) => 
        record.sector_nombre === String(value),
    },
    {
      title: 'Equipo',
      dataIndex: 'equipo_nombre',
      key: 'equipo_nombre',
      width: 35,
      filters: Array.from(new Set(ordenes.map(orden => orden.equipo_nombre)))
        .map(equipo => ({ text: equipo, value: equipo })),
      onFilter: (value: boolean | Key | string, record: ComprobanteEgreso) => 
        record.equipo_nombre === String(value),
    },
    {
      title: 'Motor',
      dataIndex: 'motor_nombre',
      key: 'motor_nombre',
      width: 35,
      filters: Array.from(new Set(ordenes.map(orden => orden.motor_nombre)))
        .map(motor => ({ text: motor, value: motor })),
      onFilter: (value: boolean | Key | string, record: ComprobanteEgreso) => 
        record.motor_nombre === String(value),
    },
    {
      title: 'Estado',
      dataIndex: 'alta',
      key: 'alta',
      width: 10,
      align: 'center',
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value: boolean | Key, record: ComprobanteEgreso) => {
        const numValue = typeof value === 'number' ? value : Number(value);
        return record.alta === numValue;
      },
      render: (alta: number) => {
        switch(alta) {
          case 1:
            return <Tag color="green">Activo</Tag>;
          case 0:
            return <Tag color="red">Inactivo</Tag>;
          default:
            return <Tag color="default">Desconocido</Tag>;
        }
      },
    },
  ];

  const columnWidths = {
    radio: '10px',
    tipo: '20px',
    nroComprobante: '35px',
    proveedor: '50px',
    emision: '35px',
    subtotal: '30px',
    impuestoInterno: '30px',
    iva: '30px',
    percepciones: '30px',
    total: '30px',
    estado: '10px'
  };

  const { Text } = Typography;

  // Cálculo de estadísticas
  const estadisticas = useMemo(() => {
    const totalEgresos = ordenes.length;
    const totalMonetario = ordenes.reduce((sum, orden) => sum + (orden.subtotal || 0), 0);
    const promedioPorEgreso = totalEgresos > 0 ? totalMonetario / totalEgresos : 0;
    
    // Egresos del mes actual
    const ahora = new Date();
    const mesActual = ahora.getMonth();
    const añoActual = ahora.getFullYear();
    const egresosMesActual = ordenes.filter(orden => {
      const fecha = new Date(orden.fecha_emision);
      return fecha.getMonth() === mesActual && fecha.getFullYear() === añoActual;
    });
    const totalMesActual = egresosMesActual.reduce((sum, orden) => sum + (orden.subtotal || 0), 0);
    
    // Egresos del mes anterior
    const mesAnterior = mesActual === 0 ? 11 : mesActual - 1;
    const añoAnterior = mesActual === 0 ? añoActual - 1 : añoActual;
    const egresosMesAnterior = ordenes.filter(orden => {
      const fecha = new Date(orden.fecha_emision);
      return fecha.getMonth() === mesAnterior && fecha.getFullYear() === añoAnterior;
    });
    const totalMesAnterior = egresosMesAnterior.reduce((sum, orden) => sum + (orden.subtotal || 0), 0);
    const variacionPorcentual = totalMesAnterior > 0 
      ? ((totalMesActual - totalMesAnterior) / totalMesAnterior) * 100 
      : 0;
    
    // Distribución por sector
    const porSector = ordenes.reduce((acc, orden) => {
      const sector = orden.sector_nombre || 'Sin sector';
      if (!acc[sector]) {
        acc[sector] = { cantidad: 0, total: 0 };
      }
      acc[sector].cantidad += 1;
      acc[sector].total += orden.subtotal || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; total: number }>);
    
    const distribucionSector = Object.entries(porSector)
      .map(([sector, datos]) => ({
        sector,
        cantidad: datos.cantidad,
        total: datos.total,
        porcentaje: totalMonetario > 0 ? (datos.total / totalMonetario) * 100 : 0
      }))
      .sort((a, b) => b.total - a.total);
    
    // Egresos por mes (últimos 6 meses)
    const meses = [];
    for (let i = 5; i >= 0; i--) {
      const fecha = new Date();
      fecha.setMonth(fecha.getMonth() - i);
      const mes = fecha.getMonth();
      const año = fecha.getFullYear();
      const nombreMes = fecha.toLocaleString('es-AR', { month: 'short' });
      
      const egresosMes = ordenes.filter(orden => {
        const fechaOrden = new Date(orden.fecha_emision);
        return fechaOrden.getMonth() === mes && fechaOrden.getFullYear() === año;
      });
      
      meses.push({
        mes: nombreMes,
        año: año.toString(),
        cantidad: egresosMes.length,
        total: egresosMes.reduce((sum, orden) => sum + (orden.subtotal || 0), 0)
      });
    }
    
    // Activos vs Inactivos
    const activos = ordenes.filter(orden => orden.alta === 1).length;
    const inactivos = ordenes.filter(orden => orden.alta === 0).length;
    
    return {
      totalEgresos,
      totalMonetario,
      promedioPorEgreso,
      egresosMesActual: egresosMesActual.length,
      totalMesActual,
      totalMesAnterior,
      variacionPorcentual,
      distribucionSector,
      egresosPorMes: meses,
      activos,
      inactivos
    };
  }, [ordenes]);

  // Formatear moneda
  const formatearMoneda = (valor: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(valor);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Comprobantes de Egreso
      </h1>

      <Card
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }
        }}
        title="Comprobantes de Egreso"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOrdenCompraNueva}
            size="small"
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '120px'
            }}
          >
            Nuevo
          </Button>
        }
      >
      <div style={{ padding: '24px' }}>
        <Tabs
          defaultActiveKey="listado"
          size="small"
          items={[
            {
              key: 'listado',
              label: 'Listado',
              children: (
                <>
                  <style>
                    {`
                      .hidden-column {
                        display: none;
                      }
                    `}
                  </style>
                  <Table
        loading={loading || isLoading}
        columns={columns}
        dataSource={ordenes}
        rowKey="id"
        scroll={{ x: 1300 }}
        pagination={{
          current: currentPage,
          pageSize,
          total: ordenes.length,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
          pageSizeOptions: ['4', '8', '12', '16'],
          defaultPageSize: 4,
          onChange: (page, pageSize) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          },
          onShowSizeChange: (current, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          position: ['bottomRight'],
          itemRender: (page, type, originalElement) => {
            if (type === 'prev') {
            }
            return originalElement;
          },
        }}
        summary={(pageData: readonly ComprobanteEgreso[]) => {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                Total
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}></Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
        footer={() => (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.colors.borderLight}`
          }}>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEditar}
              disabled={selectedRowId === null || ordenes.find(orden => orden.id === selectedRowId)?.alta === 2}
              size="small"
              style={{
                fontWeight: 500,
                minWidth: '120px',
                opacity: (selectedRowId !== null && ordenes.find(orden => orden.id === selectedRowId)?.alta !== 2) ? 1 : 0.6
              }}
            >
              Editar
            </Button>
            <Button
              icon={<PrinterOutlined />}
              onClick={handleImprimir}
              disabled={selectedRowId === null}
              size="small"
              style={{
                fontWeight: 500,
                minWidth: '120px',
                opacity: selectedRowId !== null ? 1 : 0.6
              }}
            >
              Imprimir
            </Button>
          </div>
        )}
        onChange={(pagination, filters, sorter) => {
          console.log('Cambios en la tabla:', { pagination, filters, sorter });
          if (pagination.current) {
            setCurrentPage(pagination.current);
          }
          if (pagination.pageSize) {
            setPageSize(pagination.pageSize);
          }
        }}
        onRow={(record) => ({
          onClick: () => setSelectedRowId(record.id),
        })}
      />
                </>
              )
            },
            {
              key: 'estadisticas',
              label: 'Estadísticas',
              children: (
                <div style={{ padding: '20px 0' }}>
                  {/* Tarjetas de resumen */}
                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Total de Egresos"
                          value={estadisticas.totalEgresos}
                          prefix={<FileTextOutlined />}
                          valueStyle={{ color: theme.colors.primary }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Total Monetario"
                          value={formatearMoneda(estadisticas.totalMonetario)}
                          prefix={<DollarOutlined />}
                          valueStyle={{ color: theme.colors.success }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Promedio por Egreso"
                          value={formatearMoneda(estadisticas.promedioPorEgreso)}
                          prefix={<BarChartOutlined />}
                          valueStyle={{ color: theme.colors.info }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Egresos del Mes"
                          value={estadisticas.egresosMesActual}
                          suffix={`(${formatearMoneda(estadisticas.totalMesActual)})`}
                          prefix={<CalendarOutlined />}
                          valueStyle={{ color: theme.colors.warning }}
                        />
                        {estadisticas.variacionPorcentual !== 0 && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: estadisticas.variacionPorcentual > 0 ? theme.colors.error : theme.colors.success,
                            marginTop: '4px'
                          }}>
                            {estadisticas.variacionPorcentual > 0 ? '↑' : '↓'} {Math.abs(estadisticas.variacionPorcentual).toFixed(1)}% vs mes anterior
                          </div>
                        )}
                      </Card>
                    </Col>
                  </Row>

                  {/* Distribución por Sector */}
                  <Card 
                    title="Distribución por Sector" 
                    style={{ marginBottom: '24px' }}
                  >
                    <style>
                      {`
                        .distribucion-sector-table .ant-table-thead > tr > th {
                          color: white !important;
                        }
                        .distribucion-sector-table .ant-table-tbody > tr > td {
                          color: white !important;
                        }
                      `}
                    </style>
                    <Table
                      className="distribucion-sector-table"
                      dataSource={estadisticas.distribucionSector}
                      rowKey="sector"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Sector',
                          dataIndex: 'sector',
                          key: 'sector',
                          render: (text: string) => <span style={{ color: 'white' }}>{text}</span>,
                        },
                        {
                          title: 'Cantidad',
                          dataIndex: 'cantidad',
                          key: 'cantidad',
                          align: 'right' as const,
                          render: (text: number) => <span style={{ color: 'white' }}>{text}</span>,
                        },
                        {
                          title: 'Total',
                          dataIndex: 'total',
                          key: 'total',
                          align: 'right' as const,
                          render: (valor: number) => <span style={{ color: 'white' }}>{formatearMoneda(valor)}</span>,
                        },
                        {
                          title: 'Porcentaje',
                          dataIndex: 'porcentaje',
                          key: 'porcentaje',
                          align: 'right' as const,
                          render: (valor: number) => <span style={{ color: 'white' }}>{`${valor.toFixed(1)}%`}</span>,
                        },
                      ]}
                    />
                  </Card>

                  {/* Egresos por Mes */}
                  <Card 
                    title="Egresos por Mes (Últimos 6 meses)" 
                    style={{ marginBottom: '24px' }}
                  >
                    <Table
                      dataSource={estadisticas.egresosPorMes}
                      rowKey={(record) => `${record.mes}-${record.año}`}
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Mes',
                          dataIndex: 'mes',
                          key: 'mes',
                        },
                        {
                          title: 'Cantidad',
                          dataIndex: 'cantidad',
                          key: 'cantidad',
                          align: 'right' as const,
                        },
                        {
                          title: 'Total',
                          dataIndex: 'total',
                          key: 'total',
                          align: 'right' as const,
                          render: (valor: number) => formatearMoneda(valor),
                        },
                      ]}
                    />
                  </Card>

                  {/* Estado de Comprobantes */}
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Card>
                        <Statistic
                          title="Comprobantes Activos"
                          value={estadisticas.activos}
                          valueStyle={{ color: theme.colors.success }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Card>
                        <Statistic
                          title="Comprobantes Inactivos"
                          value={estadisticas.inactivos}
                          valueStyle={{ color: theme.colors.error }}
                        />
                      </Card>
                    </Col>
                  </Row>
                </div>
              )
            }
          ]}
        />
      </div>
      </Card>
    </div>
  );
};

export default ListarEgresos;